import { useNavigate } from "react-router-dom";
import { GeneralCommonHeader } from "../../components/common/GeneralCommonHeader";
import {
  DISPLAY_HEADER_ICON_BACK,
  NUMBER_OF_ITEMS_DISPLAYED_PER_PAGE,
  DISPLAY_PAGE_FAVORITE,
} from "../../constants/common_constants";
import { DisPlayFavoriteList } from "../../features/components/favorite/ui_elements/DisplayFavoriteList";
import { Pager } from "../common/Pager";
import { useApi, useErrorDialog, useInitUsageLog } from "../../hooks";
import { useEffect, useRef, useState } from "react";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { useTranslation } from "react-i18next";
import { EditDialog } from "../common/EditDialog";
import { calcUtcDatetime } from "../../utils/basis_util";
import {
  IFavoriteInfoListType,
  IFavoriteInfoType,
} from "../../features/components/favorite/types/favorite_types";
import { SortOrder, useDtsAppContext } from "../provider/DtsAppContextProvider";
import {
  deleteFavoriteInfo,
  getFavoriteInfoList,
  updateFavoriteTitle,
} from "../../features/api/dts_api_wrapper";

//お気に入り画面
export const Favorite = () => {
  // ページ遷移用
  const navigate = useNavigate();
  //言語切替用
  const { t } = useTranslation();
  //API用
  const { handleApi } = useApi();
  //お気に入り情報リストのrefを取得
  const listElement = useRef<HTMLDivElement>(null);

  //ユーザー情報等
  const {
    dacFavoriteSortOrder,
    setDacFavoriteSortOrder,
    dacFavoritePageNum,
    setDacFavoritePageNum,
  } = useDtsAppContext();
  const userId =
    localStorage.getItem(
      `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`
    ) || "";
  const displayPage = DISPLAY_PAGE_FAVORITE;
  const displayDateTime = calcUtcDatetime(); //現在日時(UTC時刻)

  // 1ページ当たりに表示される件数
  const numPerPage = NUMBER_OF_ITEMS_DISPLAYED_PER_PAGE;
  // 並び順項目設定用(文字列リテラル)
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  //お気に入り情報一覧を格納
  const [favoriteInfoList, setFavoriteInfoList] =
    useState<IFavoriteInfoListType>({
      favoriteInfo: [],
      totalPageNum: 1,
      pageNum: 1,
    });

  //お気に入り情報を格納(削除,更新用)
  const [favoriteInfo, setFavoriteInfo] = useState<IFavoriteInfoType>({
    id: 0,
    dmcInfo: "",
    model: "",
    title: "",
    displayDateTime: "",
  });

  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  //確認ダイアログ関連
  const [confirmMessage, setConfirmMessage] = useState("");
  const [isShowConfirmDialog, setIsShowConfirmDialog] = useState(false);
  //編集ダイアログ関連
  const [editStr, setEditStr] = useState<string>("");
  const [isShowEditDialog, setIsShowEditDialog] = useState<boolean>(false);

  const showConfirmDialog = (message: string) => {
    setConfirmMessage(message);
    setIsShowConfirmDialog(true);
  };

  const closeConfirmDialog = () => {
    setConfirmMessage("");
    setIsShowConfirmDialog(false);
  };

  const showEditDialog = (title: string) => {
    setEditStr(title);
    setIsShowEditDialog(true);
  };

  const closeEditDialog = () => {
    //state初期化
    setEditStr("");
    setFavoriteInfo({
      id: 0,
      dmcInfo: "",
      model: "",
      title: "",
      displayDateTime: "",
    });
    setIsShowEditDialog(false);
  };

  // 初期表示時の利用ログ登録
  const { handleInit: initUsageLog } = useInitUsageLog({
    userId,
    displayPage,
    displayDateTime,
  });

  //確認ダイアログCancelボタン押下時の処理
  const handleOnConfirmCancel = () => {
    closeConfirmDialog();
  };

  //確認ダイアログOKボタン押下時の処理
  const handleOnConfirmOk = async () => {
    closeConfirmDialog();
    //お気に入り情報削除・お気に入り情報一覧取得に必要なパラメータが不足している場合、エラーダイアログを表示してトップページに遷移
    if (
      !userId ||
      !favoriteInfoList.pageNum ||
      !numPerPage ||
      !sortOrder ||
      !favoriteInfo.id
    ) {
      console.log(
        `[${displayPage}] handleOnConfirmOk error: failed to delete favorite`
      );
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
        "navigateToTopPage"
      );
      return; // パラメータ不足の場合は処理を終了
    }
    try {
      //お気に入り削除
      const favoriteDeleteResult = await handleApi(
        deleteFavoriteInfo,
        userId,
        favoriteInfo.id
      );
      //お気に入り情報削除がエラーの場合
      if ("errorMessage" in favoriteDeleteResult) {
        console.log("errorMessage: ", favoriteDeleteResult.errorMessage);
        throw new Error("failed to delete favorite info");
      }
      //お気に入り情報一覧取得のAPI
      await handleGetFavoriteInfoList(favoriteInfoList.pageNum, sortOrder);
    } catch (err) {
      console.log(`[${displayPage}] handleOnConfirmOk error:`, err);
      if (
        err instanceof Error &&
        (err.message === "Expired token" || err.message === "Invalid token")
      ) {
        //LocalStorageのセッション情報クリア
        const userpool_client_id =
          process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID ?? "";
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.accessToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.refreshToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.idToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.clockDrift`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.LastAuthUser`
        );

        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.セッションがタイムアウトしました。"),
          "ydsyos"
        ); //エラー", "セッションがタイムアウトしました。(YDS/YOSに遷移する)
      } else {
        // セッション切れ、パラメータ不足以外のエラー時にはその場に留まる
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。")
        ); //エラー", "通信エラーが発生しました。
      }
    }
  };

  //編集ダイアログCancelボタン押下時の処理
  const handleOnEditCancel = () => {
    closeEditDialog();
  };

  //編集ダイアログOKボタン押下時の処理
  const handleOnEditOk = async () => {
    closeEditDialog();
    //お気に入りタイトル更新・お気に入り情報一覧取得に必要なパラメータが不足している場合、エラーダイアログを表示してトップページに遷移
    if (
      !userId ||
      !favoriteInfo.id ||
      !favoriteInfo.model ||
      !favoriteInfoList.pageNum ||
      !numPerPage ||
      !sortOrder ||
      !editStr
    ) {
      //タイトルが未入力の場合, タイトル未入力のダイアログを出し、遷移しない
      if (!editStr) {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("お気に入り画面.タイトルが未入力です。")
        ); //エラー", "タイトルが未入力です。
      } else {
        console.log(
          `[${displayPage}] handleOnEditOk error: failed to update favorite`
        );
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。"),
          "navigateToTopPage"
        ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
      }
      return; // パラメータ不足の場合は処理を終了
    }
    try {
      //お気に入りタイトル更新
      const favoriteInfoUpdateResult = await handleApi(
        updateFavoriteTitle,
        userId,
        favoriteInfo.id,
        favoriteInfo.model,
        editStr
      );
      //お気に入りタイトル更新がエラーの場合
      if ("errorMessage" in favoriteInfoUpdateResult) {
        console.log("errorMessage: ", favoriteInfoUpdateResult.errorMessage);
        if (
          favoriteInfoUpdateResult.errorMessage === "Duplicated Title Error"
        ) {
          //タイトル重複
          throw new Error("Duplicated Title Error");
        } else {
          throw new Error("failed to update favorite title");
        }
      }
      //お気に入り情報一覧取得のAPI
      handleGetFavoriteInfoList(favoriteInfoList.pageNum, sortOrder);
    } catch (err) {
      console.log(`[${displayPage}] handleOnEditOk error:`, err);
      // セッション切れ、パラメータ不足以外のエラー時にはその場に留まる
      if (err instanceof Error && err.message === "Duplicated Title Error") {
        //タイトル重複の場合
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("お気に入り画面.登録済みのタイトルです。"),
          ""
        ); //エラー", "登録済みのタイトルです。
      } else if (
        err instanceof Error &&
        (err.message === "Expired token" || err.message === "Invalid token")
      ) {
        //LocalStorageのセッション情報クリア
        const userpool_client_id =
          process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID ?? "";
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.accessToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.refreshToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.idToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.clockDrift`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.LastAuthUser`
        );

        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.セッションがタイムアウトしました。"),
          "ydsyos"
        ); //エラー", "セッションがタイムアウトしました。(YDS/YOSに遷移する)
      } else {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。")
        ); //エラー", "通信エラーが発生しました。
      }
    }
  };

  // ページ切り替え時
  const handlePageChange = async (page: number) => {
    // 必要なパラメータが不足している場合、エラーダイアログを表示してトップページに遷移
    if (!userId || !page || !numPerPage || !sortOrder) {
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
        "navigateToTopPage"
      ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
      return; // パラメータ不足の場合は処理を終了
    }
    returnTop();
    try {
      //お気に入り情報一覧を取得
      await handleGetFavoriteInfoList(page, sortOrder);
    } catch (err) {
      console.log(`[${displayPage}] handlePageChange error:`, err);
      if (
        err instanceof Error &&
        (err.message === "Expired token" || err.message === "Invalid token")
      ) {
        //LocalStorageのセッション情報クリア
        const userpool_client_id =
          process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID ?? "";
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.accessToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.refreshToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.idToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.clockDrift`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.LastAuthUser`
        );

        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.セッションがタイムアウトしました。"),
          "ydsyos"
        ); //エラー", "セッションがタイムアウトしました。(YDS/YOSに遷移する)
      } else {
        // セッション切れ、パラメータ不足以外のエラー時にはその場に留まる
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。")
        ); //エラー", "通信エラーが発生しました。
      }
    }
  };

  //Context or ローカルストレージからデータ取得, お気に入り情報一覧取得(初回)
  const getInitialInfo = async () => {
    try {
      // 利用ログ登録
      const isError = await initUsageLog();
      if (isError) {
        return;
      }
      //並び順、ページ番号をContext or ローカルストレージから読み出す
      const _sortOrder =
        localStorage.getItem("favoriteSortOrder") || dacFavoriteSortOrder || "";
      const _pageNum =
        Number(localStorage.getItem("favoritePageNum")) ||
        dacFavoritePageNum ||
        NaN;
      if (_sortOrder && _pageNum) {
        //読み出し成功した場合, stateに保存
        setSortOrder(_sortOrder as SortOrder);
        setFavoriteInfoList((prevState) => ({
          ...prevState,
          pageNum: _pageNum,
        }));
        //ページ更新等でContextが初期化された場合, ContextにLocalstorageの値をセット
        if (
          dacFavoriteSortOrder !== (_sortOrder as SortOrder) ||
          !dacFavoritePageNum
        ) {
          setDacFavoriteSortOrder(_sortOrder as SortOrder);
          setDacFavoritePageNum(_pageNum);
        }
        //お気に入り情報一覧の取得(初回)
        await handleGetFavoriteInfoList(_pageNum, _sortOrder as SortOrder);
      } else {
        //Context、LocalStorageのどちらからも取得できなかった場合, state、Context、LocalStorageに初期値を格納
        setSortOrder("desc");
        setFavoriteInfoList((prevState) => ({ ...prevState, pageNum: 1 }));
        setDacSortOrderAndPageNum(1, "desc");
        //初期値での検索履歴一覧の取得
        await handleGetFavoriteInfoList(1, "desc");
      }
    } catch (err) {
      console.log(`[${displayPage}]getInitialInfo err: `, err);
      if (
        err instanceof Error &&
        (err.message === "Expired token" || err.message === "Invalid token")
      ) {
        //LocalStorageのセッション情報クリア
        const userpool_client_id =
          process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID ?? "";
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.accessToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.refreshToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.idToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.clockDrift`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.LastAuthUser`
        );

        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.セッションがタイムアウトしました。"),
          "ydsyos"
        ); //エラー", "セッションがタイムアウトしました。(YDS/YOSに遷移する)
      } else {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。"),
          "navigateToTopPage"
        ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
      }
    }
  };

  //お気に入り情報一覧の取得(呼び出し元でのエラー処理必須)
  const handleGetFavoriteInfoList = async (
    pageNum: number,
    sortOrder: SortOrder
  ) => {
    //お気に入り情報一覧を取得
    const favoriteInfoListResult = await handleApi(
      getFavoriteInfoList,
      userId,
      pageNum,
      numPerPage,
      sortOrder
    );
    //お気に入り情報一覧の取得でエラーが出た場合
    if ("errorMessage" in favoriteInfoListResult) {
      console.log("errorMessage: ", favoriteInfoListResult.errorMessage);
      throw new Error("failed to get favorite info list");
    }
    //お気に入り情報一覧をstateに格納
    const favoriteInfoListStr = JSON.stringify(favoriteInfoListResult);
    const favoriteInfoListJson: IFavoriteInfoListType =
      JSON.parse(favoriteInfoListStr);
    setFavoriteInfoList(favoriteInfoListJson);
    setSortOrder(sortOrder);
    //ページ番号と並び順をContext + Localstorageに保存
    setDacSortOrderAndPageNum(favoriteInfoListJson.pageNum, sortOrder);
  };

  //並び順, ページ番号をContext + localStorageに保存する関数
  const setDacSortOrderAndPageNum = (pageNum: number, sortOrder: SortOrder) => {
    setDacFavoriteSortOrder(sortOrder);
    localStorage.setItem("favoriteSortOrder", sortOrder);
    setDacFavoritePageNum(pageNum);
    localStorage.setItem("favoritePageNum", String(pageNum));
  };

  useEffect(() => {
    //初回のデータ取得処理
    getInitialInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //検索履歴一覧のスクロールバーをトップに移動させる関数
  const returnTop = () => {
    listElement.current?.scrollTo({ top: 0 });
  };

  //お気に入りリストをレンダリングする関数
  const renderFavoriteList = () => {
    return favoriteInfoList.favoriteInfo.map((favoriteInfo, index) => (
      <DisPlayFavoriteList
        favoriteInfo={favoriteInfo}
        userId={userId}
        showConfirmDialog={showConfirmDialog}
        showEditDialog={showEditDialog}
        setFavoriteInfo={setFavoriteInfo}
        isLastList={favoriteInfoList.favoriteInfo.length === index + 1}
        key={favoriteInfo.id}
      />
    ));
  };

  return (
    <div className="flex flex-col h-svh overflow-hidden">
      {/* ヘッダー */}
      <GeneralCommonHeader
        title={t("お気に入り画面.お気に入り")}
        displayheaderIcon={DISPLAY_HEADER_ICON_BACK}
        onBtnClick={() => {
          navigate(-1);
        }}
      />
      {/* 画面 */}
      <div className="flex flex-col mx-4 mt-4 h-full overflow-hidden">
        <div
          className="h-full overflow-y-scroll overflow-x-hidden"
          ref={listElement}
        >
          {favoriteInfoList.favoriteInfo.length ? (
            renderFavoriteList()
          ) : (
            <p className="h-full flex items-center justify-center">
              {t("お気に入り画面.お気に入り項目がありません。")}
            </p>
          )}
        </div>
      </div>
      <Pager
        currentPage={favoriteInfoList.pageNum}
        totalPages={favoriteInfoList.totalPageNum}
        onPageChange={handlePageChange}
      />
      {isShowConfirmDialog && (
        <ConfirmDialog
          message={confirmMessage}
          onCancel={handleOnConfirmCancel}
          onOk={handleOnConfirmOk}
        ></ConfirmDialog>
      )}
      {isShowEditDialog && (
        <EditDialog
          message={t("お気に入り画面.タイトル編集")}
          editStr={editStr}
          setEditStr={setEditStr}
          onCancel={handleOnEditCancel}
          onOk={handleOnEditOk}
        />
      )}
    </div>
  );
};
